// The capture window (60.11.580): a small window of the panel's own that holds a ticket's screen share and screen
// recording, so both outlive the page they were started on - a refresh, another business, a saved invoice, even the
// panel's tab closed. The panel's tabs see and steer them through a BroadcastChannel (capture-hub.ts).

export const CAPTURE_PATH = '/screen-capture'
const WINDOW_NAME = 'asa-screen-capture'
const CHANNEL_NAME = 'asa-screen-capture'
const FEATURES = 'popup,width=460,height=660'
/** How long a page waits for a capture window it opened to answer before it lets the request go. */
export const CAPTURE_WINDOW_WAIT_MS = 15_000

export type CaptureRequest = {kind: 'share' | 'record'; ticketNumber: string; microphone?: boolean}

/** Whether this page is the capture window itself. */
export const inCaptureWindow = () =>
  typeof window !== 'undefined' && window.location.pathname === CAPTURE_PATH

let channel: BroadcastChannel | null | undefined

/** The channel between the capture window and the panel's tabs; null in a browser without one. */
export const captureChannel = () => {
  if (channel === undefined) {
    try {
      channel = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(CHANNEL_NAME)
    } catch {
      channel = null
    }
  }
  return channel
}

export const captureWindowUrl = (request: CaptureRequest) =>
  `${CAPTURE_PATH}?${new URLSearchParams({
    kind: request.kind,
    ticket: request.ticketNumber,
    ...(request.microphone ? {mic: '1'} : {}),
  }).toString()}`

/** A window just opened by name, with nothing in it yet. */
const isBlank = (opened: Window) => {
  try {
    return opened.location.href === 'about:blank'
  } catch {
    return false
  }
}

/** The window by its name: the one already open, or a new blank one; null where the browser opens none. */
const windowByName = () => {
  try {
    return window.open('', WINDOW_NAME, FEATURES)
  } catch {
    return null
  }
}

/**
 * The capture window with this request - opened, or, when it is open already, brought forward with it. False where the
 * browser opens no window (a blocked popup): the capture then runs in the page itself.
 */
export const openCaptureWindow = (request: CaptureRequest) => {
  if (inCaptureWindow() || !captureChannel()) return false
  const opened = windowByName()
  if (!opened) return false
  if (isBlank(opened)) opened.location.replace(captureWindowUrl(request))
  else captureChannel()?.postMessage({type: 'prepare', request})
  try {
    opened.focus()
  } catch {
    // Brought forward by the browser's own choice.
  }
  return true
}

/** «نمایش پنجره»: the capture window brought forward; false when it is not open. */
export const focusCaptureWindow = () => {
  const opened = windowByName()
  if (!opened) return false
  if (isBlank(opened)) {
    opened.close()
    return false
  }
  try {
    opened.focus()
  } catch {
    // Brought forward by the browser's own choice.
  }
  return true
}
